import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { alpha, darken } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { resolveEntityTags } from '@/features/shared/tags';
import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s, typographyTokens } from '@/theme/tokens';
import {
  Chip,
  formatDuration,
  formatStatusLabel,
  getStatusTextColor,
  Inline,
  Link,
  resolveStatusChipTone,
  Text,
  Tooltip,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { getStatusIconComponent, TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, type GridColDef, type GridRenderCellParams, type Theme } from '@/ui/primitives';
import { CloneCellContainer } from './testsColumns.cloneCells';
import type { TagColor } from '@/shared/data/tags';
import type { TestExecutionStatus, TestListItemDto } from '@flowqa/shared';

type BuildCoreColumnsOptions = {
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: TestExecutionStatus | null) => void;
  showSuiteLinks: boolean;
  combineNameAndSuite: boolean;
  includeSuiteInNameColumn: boolean;
  includeTagsInNameColumn: boolean;
  latestAsMeta: boolean;
  includeLastExecutedColumn: boolean;
  disableLinks: boolean;
  tagColorByName?: Record<string, TagColor>;
  columnFlex: {
    name: number;
    suite: number;
    latest: number;
    lastExecuted: number;
  };
};

const NUMERIC_TEXT_SX = {};
const TEST_NAME_TO_SUITE_GAP = '-6px';
const TEST_SUITE_TO_TAGS_GAP = s('xs');
const LATEST_META_DETAIL_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: s('xs'),
  pl: s('xxs'),
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
};
const LATEST_META_DETAIL_VALUE_SX = {
  display: 'block',
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const LATEST_META_DETAIL_TEXT_SX = {
  '&&': {
    fontSize: typographyTokens.captionSmall.fontSize,
    fontWeight: typographyTokens.captionSmall.fontWeight,
    lineHeight: 1.05,
  },
};
const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: (theme: Theme) => theme.typography.body2.fontSize,
      lineHeight: (theme: Theme) => theme.typography.body2.lineHeight,
    },
  },
} as const;

export const resolveLatestChipForeground = (
  theme: Parameters<typeof resolveSemanticColor>[0],
  statusTone: string,
): string => {
  if (isLightTheme(theme)) {
    if (statusTone === 'green' || statusTone === 'success') {
      return darken(resolveSemanticColor(theme, 'success'), 0.5);
    }
    if (statusTone === 'red' || statusTone === 'error') {
      return darken(resolveSemanticColor(theme, 'error'), 0.5);
    }
    if (statusTone === 'orange' || statusTone === 'warning' || statusTone === 'brown') {
      return darken(resolveSemanticColor(theme, 'warning'), 0.52);
    }
    if (statusTone === 'blue' || statusTone === 'info') {
      return darken(resolveSemanticColor(theme, 'info'), 0.5);
    }
    if (statusTone === 'purple') {
      return darken(resolveSemanticColor(theme, 'accent'), 0.5);
    }
    if (statusTone === 'teal') {
      return darken(resolveSemanticColor(theme, 'accentStrong'), 0.5);
    }
    return darken(resolveSemanticColor(theme, 'textPrimary'), 0.24);
  }

  if (statusTone === 'green' || statusTone === 'success') {
    return darken(resolveSemanticColor(theme, 'success'), 0.72);
  }
  if (statusTone === 'red' || statusTone === 'error') {
    return darken(resolveSemanticColor(theme, 'error'), 0.72);
  }
  if (statusTone === 'orange' || statusTone === 'warning' || statusTone === 'brown') {
    return darken(resolveSemanticColor(theme, 'warning'), 0.74);
  }
  if (statusTone === 'blue' || statusTone === 'info') {
    return darken(resolveSemanticColor(theme, 'info'), 0.72);
  }
  if (statusTone === 'purple') {
    return darken(resolveSemanticColor(theme, 'accent'), 0.72);
  }
  if (statusTone === 'teal') {
    return darken(resolveSemanticColor(theme, 'accentStrong'), 0.72);
  }
  return darken(resolveSemanticColor(theme, 'textPrimary'), 0.56);
};

export const resolveLatestChipBackground = (
  theme: Parameters<typeof resolveSemanticColor>[0],
  statusTone: string,
): string => {
  if (isLightTheme(theme)) {
    if (statusTone === 'green' || statusTone === 'success') {
      return alpha(resolveSemanticColor(theme, 'success'), 0.34);
    }
    if (statusTone === 'red' || statusTone === 'error') {
      return alpha(resolveSemanticColor(theme, 'error'), 0.32);
    }
    if (statusTone === 'orange' || statusTone === 'warning' || statusTone === 'brown') {
      return alpha(resolveSemanticColor(theme, 'warning'), 0.34);
    }
    if (statusTone === 'blue' || statusTone === 'info') {
      return alpha(resolveSemanticColor(theme, 'info'), 0.3);
    }
    if (statusTone === 'purple') {
      return alpha(resolveSemanticColor(theme, 'accent'), 0.3);
    }
    if (statusTone === 'teal') {
      return alpha(resolveSemanticColor(theme, 'accentStrong'), 0.3);
    }
    return alpha(resolveSemanticColor(theme, 'surfaceSubtle'), 0.72);
  }

  if (statusTone === 'green' || statusTone === 'success') {
    return darken(resolveSemanticColor(theme, 'success'), 0.23);
  }
  if (statusTone === 'red' || statusTone === 'error') {
    return darken(resolveSemanticColor(theme, 'error'), 0.23);
  }
  if (statusTone === 'orange' || statusTone === 'warning' || statusTone === 'brown') {
    return darken(resolveSemanticColor(theme, 'warning'), 0.21);
  }
  if (statusTone === 'blue' || statusTone === 'info') {
    return darken(resolveSemanticColor(theme, 'info'), 0.23);
  }
  if (statusTone === 'purple') {
    return darken(resolveSemanticColor(theme, 'accent'), 0.23);
  }
  if (statusTone === 'teal') {
    return darken(resolveSemanticColor(theme, 'accentStrong'), 0.23);
  }
  return darken(resolveSemanticColor(theme, 'surfaceSubtle'), 0.12);
};

export const buildCoreColumns = ({
  onProjectSelect,
  onStatusSelect,
  showSuiteLinks,
  combineNameAndSuite,
  includeSuiteInNameColumn,
  includeTagsInNameColumn,
  latestAsMeta,
  includeLastExecutedColumn,
  disableLinks,
  tagColorByName = {},
  columnFlex,
}: BuildCoreColumnsOptions): {
  nameColumn: GridColDef<TestListItemDto>;
  suiteColumn: GridColDef<TestListItemDto>;
  latestSummaryColumn: GridColDef<TestListItemDto>;
  lastExecutedColumn: GridColDef<TestListItemDto, Date | string | null> | null;
} => {
  const stringOperators = getStringFilterOperators();

  const nameColumn: GridColDef<TestListItemDto> = {
    field: 'name',
    headerName: 'Test',
    flex: columnFlex.name,
    align: 'left',
    headerAlign: 'left',
    cellClassName: combineNameAndSuite ? 'tests-table-clone-test-cell' : undefined,
    filterOperators: stringOperators,
    renderCell: (params) => {
      if (combineNameAndSuite) {
        const primarySuite = params.row.suites?.primary ?? params.row.suites?.items?.[0] ?? null;
        const testHref = disableLinks ? null : `/tests/${params.row.slug}`;
        const suiteHref =
          !disableLinks && showSuiteLinks && primarySuite ? `/suites/${primarySuite.slug}` : null;
        const tags = resolveEntityTags(params.row);
        return (
          <CloneCellContainer gap="none" pt="4px">
            {testHref ? (
              <Text role="body" sx={{ width: '100%', minWidth: 0 }}>
                <OverflowTooltip title={params.row.name}>
                  <Link
                    component={RouterLink}
                    to={testHref}
                    underline="hover"
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                      onProjectSelect(params.row.projectId);
                    }}
                    sx={{
                      display: 'inline-block',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {params.row.name}
                  </Link>
                </OverflowTooltip>
              </Text>
            ) : (
              <OverflowTooltip title={params.row.name}>
                <Text role="body" truncate sx={{ display: 'block', width: '100%', minWidth: 0 }}>
                  {params.row.name}
                </Text>
              </OverflowTooltip>
            )}
            {includeSuiteInNameColumn && primarySuite ? (
              suiteHref ? (
                <Text
                  role="body"
                  color="textSecondary"
                  sx={{ width: '100%', minWidth: 0, mt: TEST_NAME_TO_SUITE_GAP, lineHeight: 1.1 }}
                >
                  <OverflowTooltip title={primarySuite.name}>
                    <Link
                      component={RouterLink}
                      to={suiteHref}
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        display: 'inline-block',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {primarySuite.name}
                    </Link>
                  </OverflowTooltip>
                </Text>
              ) : (
                <OverflowTooltip title={primarySuite.name}>
                  <Text
                    role="body"
                    color="textSecondary"
                    truncate
                    sx={{
                      display: 'block',
                      width: '100%',
                      minWidth: 0,
                      mt: TEST_NAME_TO_SUITE_GAP,
                      lineHeight: 1.1,
                    }}
                  >
                    {primarySuite.name}
                  </Text>
                </OverflowTooltip>
              )
            ) : null}
            {includeTagsInNameColumn && tags.length > 0 ? (
              <Box sx={{ width: '100%', minWidth: 0, mt: TEST_SUITE_TO_TAGS_GAP }}>
                <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      size="xs"
                      tone={(() => {
                        const trimmed = tag.trim();
                        const fallback = trimmed.toLowerCase();
                        if (Object.prototype.hasOwnProperty.call(tagColorByName, trimmed)) {
                          return tagColorByName[trimmed];
                        }
                        if (Object.prototype.hasOwnProperty.call(tagColorByName, fallback)) {
                          return tagColorByName[fallback];
                        }
                        return 'neutral';
                      })()}
                      label={tag}
                      sx={{ borderRadius: r('pill') }}
                    />
                  ))}
                </Inline>
              </Box>
            ) : null}
          </CloneCellContainer>
        );
      }

      return (
        <TableTextCell
          primary={{
            label: params.row.name,
            ...(disableLinks
              ? {}
              : {
                  href: `/tests/${params.row.slug}`,
                  onClick: () => {
                    onProjectSelect(params.row.projectId);
                  },
                }),
          }}
        />
      );
    },
  };

  const suiteColumn: GridColDef<TestListItemDto> = {
    field: 'suiteName',
    headerName: 'Suite',
    flex: columnFlex.suite,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<TestListItemDto>) => {
      if (!showSuiteLinks) {
        return <TagsInlineCell tags={[]} />;
      }
      const suiteItems = params.row.suites?.items ?? [];
      const suiteIds = params.row.suites?.ids ?? [];
      const tags = suiteItems.map((suite) => suite.name);
      const suiteLinks: Array<{ label: string; href: string }> = [];
      suiteItems.forEach((suite) => {
        const href = `/suites/${suite.slug}`;
        if (suiteLinks.some((item) => item.href === href)) return;
        suiteLinks.push({ label: suite.name, href });
      });
      if (suiteLinks.length > 0) {
        return (
          <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <Inline
              gap="xs"
              wrap="nowrap"
              align="center"
              sx={{ width: '100%', minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {suiteLinks.map((suite, index) =>
                disableLinks ? (
                  <Text
                    key={suite.href}
                    role="body"
                    truncate
                    component="span"
                    sx={{ display: 'inline-block', minWidth: 0, maxWidth: '100%' }}
                  >
                    {index < suiteLinks.length - 1 ? `${suite.label},` : suite.label}
                  </Text>
                ) : (
                  <Text
                    key={suite.href}
                    role="body"
                    component="span"
                    sx={{ display: 'inline-block', minWidth: 0, maxWidth: '100%' }}
                  >
                    <Link
                      component={RouterLink}
                      to={suite.href}
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        display: 'inline-block',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 1,
                      }}
                    >
                      {index < suiteLinks.length - 1 ? `${suite.label},` : suite.label}
                    </Link>
                  </Text>
                ),
              )}
            </Inline>
          </Box>
        );
      }
      const fallbackTags = tags.length > 0 ? tags : suiteIds;
      return <TagsInlineCell tags={fallbackTags} />;
    },
  };

  const latestSummaryColumn: GridColDef<TestListItemDto> = {
    field: 'latestExecutionStatus',
    headerName: latestAsMeta ? 'Latest' : 'Lastest Status',
    flex: latestAsMeta ? 0.48 : columnFlex.latest,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'tests-table-clone-latest-cell' : undefined,
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<TestListItemDto>) => {
      const latestExecution = params.row.latestExecution ?? null;
      const status = latestExecution?.status ?? null;
      if (latestAsMeta) {
        const statusLabel = formatStatusLabel(status) || '-';
        const statusTone = resolveStatusChipTone(status);
        const durationLabel =
          latestExecution?.durationMs !== null && latestExecution?.durationMs !== undefined
            ? formatDuration(latestExecution.durationMs)
            : '';
        const timestamp = latestExecution?.timestamp ?? null;
        const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
        const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
        const resolvedTimestampTitle =
          timestampTitle || (timestamp instanceof Date ? timestamp.toISOString() : timestamp);
        const runNumber = latestExecution?.runNumber ?? null;
        const executionHref =
          runNumber !== null && params.row.slug && !disableLinks
            ? `/tests/${params.row.slug}/runs/${runNumber}`
            : null;
        return (
          <CloneCellContainer gap="none" pt="6px">
            <Chip
              size="xs"
              icon={<Box component={getStatusIconComponent(status)} />}
              label={
                executionHref ? (
                  <Link
                    component={RouterLink}
                    to={executionHref}
                    underline="hover"
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                      onProjectSelect(params.row.projectId);
                    }}
                  >
                    {statusLabel}
                  </Link>
                ) : (
                  statusLabel
                )
              }
              tone={statusTone}
              sx={{
                mt: s('none'),
                mb: `calc(${s('sm')} + ${s('xxs')})`,
                borderRadius: r('pill'),
                backgroundColor: (theme) => resolveLatestChipBackground(theme, statusTone),
                color: (theme) => resolveLatestChipForeground(theme, statusTone),
                '& .MuiChip-label': {
                  color: 'inherit',
                  fontWeight: 500,
                  fontSize: '13px !important',
                },
                '& .MuiChip-label .MuiTypography-root': {
                  color: 'inherit',
                  fontWeight: 'inherit',
                  fontSize: '13px !important',
                },
                '& .MuiChip-label a': {
                  fontSize: '13px !important',
                },
                '& .MuiChip-label *': {
                  fontSize: '13px !important',
                },
                '& .MuiChip-icon': {
                  color: 'inherit',
                  fontSize: (theme) => theme.app.iconSizes.sm,
                  marginRight: '-6px',
                },
              }}
            />
            {durationLabel ? (
              <Text
                role="captionSmall"
                color="textSecondary"
                dataGridTypography="custom"
                sx={LATEST_META_DETAIL_TEXT_SX}
              >
                <Box component="span" sx={LATEST_META_DETAIL_ROW_SX}>
                  <Tooltip title="Duration" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                    <TimerOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  </Tooltip>
                  <Box component="span" sx={LATEST_META_DETAIL_VALUE_SX}>
                    {durationLabel}
                  </Box>
                </Box>
              </Text>
            ) : null}
            {timestampLabel ? (
              <Text
                role="captionSmall"
                color="textSecondary"
                dataGridTypography="custom"
                sx={[
                  LATEST_META_DETAIL_TEXT_SX,
                  {
                    mt: s('xs'),
                    display: 'block',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                ]}
              >
                <Box component="span" sx={LATEST_META_DETAIL_ROW_SX}>
                  <Tooltip title="Timestamp" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                    <EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  </Tooltip>
                  <Tooltip
                    title={resolvedTimestampTitle}
                    disableHoverListener={!timestamp}
                    arrow
                    slotProps={CLONE_TOOLTIP_SLOT_PROPS}
                  >
                    <Box component="span" sx={LATEST_META_DETAIL_VALUE_SX}>
                      {timestampLabel}
                    </Box>
                  </Tooltip>
                </Box>
              </Text>
            ) : null}
          </CloneCellContainer>
        );
      }
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'Untested';
      const runNumber = latestExecution?.runNumber ?? null;
      const executionHref =
        runNumber !== null && params.row.slug
          ? `/tests/${params.row.slug}/runs/${runNumber}`
          : null;
      const labelNode =
        executionHref && !disableLinks ? (
          <Link
            component={RouterLink}
            to={executionHref}
            underline="hover"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(params.row.projectId);
            }}
          >
            <Text role="body" component="span" color="inherit">
              {label}
            </Text>
          </Link>
        ) : (
          label
        );
      if (isClickable && !executionHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? null);
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };

  const lastExecutedColumn: GridColDef<TestListItemDto, Date | string | null> | null =
    includeLastExecutedColumn
      ? {
          field: 'latestExecutionTimestamp',
          headerName: 'Last Executed',
          flex: columnFlex.lastExecuted,
          align: 'right',
          headerAlign: 'right',
          filterable: false,
          valueGetter: (_value, row) => row.latestExecution?.timestamp ?? null,
          renderCell: (params: GridRenderCellParams<TestListItemDto, Date | string | null>) => {
            const timestamp = params.value ?? null;
            const label = timestamp ? formatRelativeTime(timestamp) : '-';
            const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
            return (
              <Tooltip
                title={fullTimestamp}
                disableHoverListener={!fullTimestamp}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      lineHeight: (theme) => theme.typography.body2.lineHeight,
                    },
                  },
                }}
              >
                <span>
                  <Text role="body" component="span" sx={NUMERIC_TEXT_SX}>
                    {label.length > 0 ? label : '-'}
                  </Text>
                </span>
              </Tooltip>
            );
          },
        }
      : null;

  return { nameColumn, suiteColumn, latestSummaryColumn, lastExecutedColumn };
};
